import {state} from "./state";
import {drawPaths} from "./paths";
import {createAjaxModal} from "../../widgets/modal";
import {createIcon} from "./icons";
import {init as initModal} from "../../widgets/modal.js";
import {init as initTooltip} from "../../widgets/tooltip.js";

export function loadPoints(points) {
    clearMarkers();
    points.forEach(point => {
        createMarker(point);
    });

    drawPaths();
}

/**
 * Enable or disable challenge opening when clicking on map markers.
 *
 * Used to prevent opening challenges while editing the map.
 *
 * @param {boolean} enabled - Whether challenge opening on click is enabled
 */
export function setChallengeClickEnabled(enabled) {
    Object.values(state.markers).forEach(marker => {
        marker
            .getElement()
            .toggleAttribute('disabled', !enabled);
    });
}

/* --------------------------------------------------
 * MAP CLICK (FIRST POINT CREATION)
 * -------------------------------------------------- */
export function enableEmptyMapClick(map) {
    map.on("click", async (e) => {
        // Strict conditions
        if (!state.editMode) return;
        if (Object.keys(state.markers).length > 0) return;

        const {lat, lng} = e.latlng;

        await openCreatePointModal({
            parent: null,
            lat,
            lng
        });
    });

    map.on("dblclick", async (e) => {
        if (!state.editMode) return;

        const {lat, lng} = e.latlng;
        await openCreatePointModal({
            parent: null,
            lat,
            lng
        });
    });
}

/* --------------------------------------------------
 * CREATE STANDARD MARKER
 * -------------------------------------------------- */
function createMarker(point) {
    let clickTimeout = null;
    const CLICK_DELAY = 200;

    const marker = L.marker([point.lat, point.lng], {
        icon: createIcon(point),
        zIndexOffset: 10,
        draggable: state.editMode
    }).addTo(state.markersGroup);

    marker.options.point = point;

    if (point.title) {
        marker.bindTooltip(point.title, {
            direction: "top",
            offset: [0, -20],
            className: "map-tooltip"
        });
    }

    marker.on("mouseover", () => {
        if (!point.locked && !state.editMode) {
            marker.getElement()?.classList.add("hover");
        }
    });

    marker.on("mouseout", () => {
        marker.getElement()?.classList.remove("hover");
    });

    marker.on("dblclick", async (e) => {
        if (!state.editMode) return;
        L.DomEvent.stop(e);
        if (clickTimeout) {
            clearTimeout(clickTimeout);
            clickTimeout = null;
        }

        await openCreatePointModal({
            parent: point.id,
            lat: point.lat + 20,
            lng: point.lng + 20
        });
    });

    marker.on("click", async (e) => {
        if (!state.editMode) return;
        L.DomEvent.stop(e);

        if (!clickTimeout) {
            clickTimeout = setTimeout(async () => {
                clickTimeout = null;
                await openEditPointModal(point);
            }, CLICK_DELAY);
        }
    });

    marker.on("contextmenu", (e) => {
        if (state.editMode)
            htmx.trigger("#map-adventure", "map:delete-point", point);
    });

    if (point.id !== undefined) {
        state.markers[point.id] = marker;
    }

    if (point.url) {
        let attrs = {
            'hx-get': point.url,
            'hx-trigger': "modalCreated",
            'hx-swap': "innerHTML",
            'data-skeleton': "#challenge-modal-skeleton",
            'data-modal-size': "max-w-4xl",
            'data-modal-title': point.title,
            'data-modal': '',
        };

        if (state.editMode) attrs['disabled'] = '';

        const el = marker.getElement();
        for (const [key, value] of Object.entries(attrs)) {
            el.setAttribute(key, value);
        }

        const parent = el.parentElement;
        initModal(parent);
        initTooltip(parent);
        htmx.process(parent);
    }

    return marker;
}

/* --------------------------------------------------
 * OPEN EDIT POINT MODAL
 * -------------------------------------------------- */
async function openEditPointModal(point) {
    await createAjaxModal({
        title: "Éditer le point",
        size: "max-w-lg",
        backdrop: true,
        ajax: {
            verb: "GET",
            path: CONFIG.urls.editPointAdventure.replace('/0', `/${point.id}`)
        }
    });
}

/* --------------------------------------------------
 * OPEN CREATE POINT MODAL
 * -------------------------------------------------- */
async function openCreatePointModal({parent, lat, lng}) {
    await createAjaxModal({
        title: "Associer un challenge",
        size: "max-w-lg",
        backdrop: true,
        ajax: {
            verb: "GET",
            path: CONFIG.urls.addPointAdventure.replace('/0/', `/${state.mapId}/`)
        }
    }).then(() => {
        document.getElementById('id_parent').value = parent;
        document.getElementById('id_x').value = lng;
        document.getElementById('id_y').value = lat;
    });
}

/* --------------------------------------------------
 * LISTENERS
 * -------------------------------------------------- */
export function initListener() {
    document.body.addEventListener("map:point-created", (evt) => {
        const point = evt.detail;

        if (state.markers[point.id]) return;

        const marker = createMarker(point);
        enableMarkerDrag(marker);
        updateEmptyOverlayState();
        drawPaths();
    });

    document.body.addEventListener("map:points-refresh", (evt) => {
        loadPoints(evt.detail.data);
    });
}

/* --------------------------------------------------
 * MARKER DRAGGING
 * -------------------------------------------------- */
export function enableMarkersDrag() {
    Object.values(state.markers).forEach(marker => {
        enableMarkerDrag(marker);
    });
}

export function disableMarkersDrag() {
    Object.values(state.markers).forEach(marker => {
        marker.dragging.disable();
    });
}

function enableMarkerDrag(marker) {
    const point = marker.options.point;
    marker.dragging.enable();

    if (marker._editModeDragEnabled) return;
    marker._editModeDragEnabled = true;

    marker.on("drag", e => {
        const latlng = e.target.getLatLng();
        point.lat = latlng.lat;
        point.lng = latlng.lng;
        state.dragging = {marker, point};
        drawPaths();
    });

    marker.on("dragend", () => {
        htmx.trigger("#map-adventure", "map:move-point", point);
    });
}

/* --------------------------------------------------
 * EMPTY STATE
 * -------------------------------------------------- */
export function updateEmptyOverlayState() {
    state.showEmptyOverlay = Object.keys(state.markers).length === 0;
    const el = document.getElementById('map-container');

    Alpine.$data(el).showEmptyOverlay = state.showEmptyOverlay;

    htmx.trigger("#map-adventure", "map:empty-state-changed", {
        empty: state.showEmptyOverlay
    });
}

/* --------------------------------------------------
 * DELETE MARKER
 * -------------------------------------------------- */
export function deleteMarker(pointId) {
    const markerToDelete = state.markers[pointId];
    if (!markerToDelete) return;

    const deletedPoint = markerToDelete.options.point;
    const newParentId = deletedPoint.parent ?? null;

    // --------------------------------------------------
    // REASSIGN CHILDREN TO NEW PARENT
    // --------------------------------------------------
    Object.values(state.markers).forEach(marker => {
        const point = marker.options.point;

        if (point.parent === pointId) {
            point.parent = newParentId;
        }
    });

    // --------------------------------------------------
    // REMOVE MARKER
    // --------------------------------------------------
    state.markersGroup.removeLayer(markerToDelete);
    delete state.markers[pointId];

    // --------------------------------------------------
    // REFRESH
    // --------------------------------------------------
    drawPaths();
    updateEmptyOverlayState();
}

function clearMarkers() {
    state.markers = {};
    state.markersGroup.clearLayers();
    state.pathsGroup.clearLayers();
}